/**
 * DataExport — live API.
 * CRUD for scheduled exports + manual trigger via /v1/system/data-exports/*.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Typography, Button, IconButton, Stack, Tooltip, Chip, TextField, TablePagination,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid, FormControl, InputLabel,
  Select, MenuItem, CircularProgress, Snackbar, Switch, FormControlLabel,
} from '@mui/material';
import { Add as AddIcon, RefreshOutlined, EditOutlined, DeleteOutlined, PlayArrowOutlined, FileDownloadOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../../api';
import { ConfirmDialog } from '../../../components/primitives';
import { translateApiError } from '../../../i18n/translateApiError';
import { activeLocale } from '../../../utils/datetime';
import { reportsApi, type ReportDefinition } from '../../../api/attendance';

interface Export {
  id: string; name: string; data_type: string; report_id: string | null;
  format: string; schedule: string | null; enabled: boolean; locale: string;
  last_run_at: string | null; last_run_row_count: number | null;
  last_run_status: string | null; has_file: boolean; created_at: string | null;
}

// Must match backend app/system/exports.py SUPPORTED_DATA_TYPES /
// SUPPORTED_FORMATS — every option here produces a real export file.
const DATA_TYPES = ['workers', 'departments', 'time_entries', 'leave_balances', 'attendance_report'];
const FORMATS = ['csv', 'json'];
const REPORT_TYPE = 'attendance_report';
const SCHEDULES = ['daily', 'weekly', 'monthly', 'quarterly'];

interface FormState {
  name: string; data_type: string; report_id: string; format: string;
  schedule: string; enabled: boolean;
}
const EMPTY: FormState = {
  name: '', data_type: 'workers', report_id: '', format: 'csv',
  schedule: 'weekly', enabled: true,
};

const DataExport: React.FC = () => {
  const { t } = useTranslation();
  const [exports, setExports] = useState<Export[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Export | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [toDelete, setToDelete] = useState<Export | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { http } = await import('../../../api/client');
      const data = await http.get<Export[]>('/v1/system/data-exports/scheduled');
      setExports(data);
    } catch { setExports([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = useMemo(() => exports.filter(e => e.name.toLowerCase().includes(filter.toLowerCase()) || e.data_type.toLowerCase().includes(filter.toLowerCase())), [exports, filter]);
  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const openAdd = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  // The report catalog, loaded once and only when it can be needed. Read from the
  // SAME endpoint the reports page uses, so a report added to the backend appears here
  // without a second list to maintain.
  const [reports, setReports] = useState<ReportDefinition[]>([]);
  useEffect(() => {
    if (!dialogOpen || reports.length > 0) return;
    reportsApi.list().then(setReports).catch(() => { /* dropdown stays empty; save is
      still validated server-side, so this cannot produce a broken schedule. */ });
  }, [dialogOpen, reports.length]);

  const openEdit = (e: Export) => { setEditing(e); setForm({ name: e.name, data_type: e.data_type, report_id: e.report_id ?? '', format: e.format, schedule: e.schedule ?? 'weekly', enabled: e.enabled }); setDialogOpen(true); };

  const handleSave = async () => {
    if (!form.name.trim() || !form.data_type) return;
    setSaving(true);
    try {
      const { http } = await import('../../../api/client');
      if (editing) {
        await http.patch(`/v1/system/data-exports/scheduled/${editing.id}`, payload());
        setToast(t('system:dataExport.toastUpdated', { name: form.name }));
      } else {
        await http.post('/v1/system/data-exports/scheduled', payload());
        setToast(t('system:dataExport.toastCreated', { name: form.name }));
      }
      setDialogOpen(false);
      await fetchAll();
    } catch (e) { setToast(e instanceof ApiError ? translateApiError(e, t) : t('system:shared.failedToSave')); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      const { http } = await import('../../../api/client');
      await http.delete(`/v1/system/data-exports/scheduled/${toDelete.id}`);
      setToDelete(null); setToast(t('system:dataExport.toastDeleted')); await fetchAll();
    } catch (e) { setToast(e instanceof ApiError ? translateApiError(e, t) : t('system:dataExport.failedToDelete')); throw e; }
  };

  /**
   * The body to send. `report_id` is sent as null for the four table exports and the
   * chosen report otherwise, because the backend validates the two fields TOGETHER: a
   * leftover report_id on a workers export is refused, and so is a report export
   * without one. Sending `form` raw would fail whenever the admin switched type.
   *
   * `locale` is the console's language, which becomes the language of the produced
   * file. A cron has no request and therefore no Accept-Language, so this is the only
   * moment it can be captured — the same reason the import templates pass `?lang=`.
   */
  const payload = () => ({
    name: form.name,
    data_type: form.data_type,
    report_id: form.data_type === REPORT_TYPE ? (form.report_id || null) : null,
    format: form.format,
    schedule: form.schedule,
    enabled: form.enabled,
    locale: activeLocale(),
  });

  const handleDownload = async (e: Export) => {
    try {
      const { http } = await import('../../../api/client');
      const res = await http.get<{ download_url?: string }>(
        `/v1/system/data-exports/scheduled/${e.id}/download`,
      );
      // Opened rather than fetched as a blob: the URL is presigned and points straight
      // at storage, so the browser downloads it without our token.
      if (res?.download_url) window.open(res.download_url, '_blank', 'noopener');
    } catch { setToast(t('system:dataExport.failedToDownload')); }
  };

  const handleManualRun = async (e: Export) => {
    try {
      const { http } = await import('../../../api/client');
      const res = await http.post<{ download_url?: string; row_count?: number }>(
        '/v1/system/data-exports/manual',
        {
          name: e.name, data_type: e.data_type, report_id: e.report_id,
          format: e.format, schedule: e.schedule, locale: e.locale || 'en',
        },
      );
      setToast(t('system:dataExport.toastManual', { name: e.name }));
      // Open the freshly-generated file (presigned URL) in a new tab so
      // the admin gets the export immediately, not just a "queued" toast.
      if (res?.download_url) window.open(res.download_url, '_blank', 'noopener');
    } catch { setToast(t('system:dataExport.failedToTrigger')); }
  };

  if (loading && exports.length === 0) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>{t('system:dataExport.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('system:dataExport.subtitle')}</Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip title={t('system:shared.refresh')}><IconButton size="small" onClick={fetchAll} sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: 1, bgcolor: '#fff', '&:hover': { bgcolor: '#f5f5f5' } }}><RefreshOutlined fontSize="small" /></IconButton></Tooltip>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openAdd} sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}>{t('system:dataExport.addExport')}</Button>
        </Stack>
      </Box>

      <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', mb: 1.5, gap: 1.5 }}>
        <TextField size="small" label={t('system:dataExport.filterLabel')} value={filter} onChange={e => { setFilter(e.target.value); setPage(0); }} sx={{ width: 300 }} />
        <Box sx={{ flex: 1 }} />
        <TablePagination component="div" count={filtered.length} page={page} onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage} onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} rowsPerPageOptions={[5, 10, 25]} sx={{ '.MuiTablePagination-toolbar': { pl: 0, minHeight: 36 } }} />
      </Box>

      <TableContainer component={Paper} sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead sx={{ bgcolor: 'grey.100', '& .MuiTableCell-root': { fontWeight: 600 } }}>
            <TableRow>
              <TableCell>{t('system:dataExport.tableHeaders.name')}</TableCell>
              <TableCell>{t('system:dataExport.tableHeaders.dataType')}</TableCell>
              <TableCell>{t('system:dataExport.tableHeaders.format')}</TableCell>
              <TableCell>{t('system:dataExport.tableHeaders.schedule')}</TableCell>
              <TableCell>{t('system:dataExport.tableHeaders.lastRun')}</TableCell>
              <TableCell align="center">{t('system:dataExport.tableHeaders.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginated.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><Typography variant="body2" color="text.secondary">{filter ? t('system:dataExport.noMatch') : t('system:dataExport.noExports')}</Typography></TableCell></TableRow>
            ) : paginated.map(e => (
              <TableRow key={e.id} hover>
                <TableCell><Typography variant="body2" sx={{ fontWeight: 600 }}>{e.name}</Typography></TableCell>
                <TableCell>
                  <Chip
                    label={
                      e.data_type === REPORT_TYPE && e.report_id
                        ? t(`attendance:attendanceReports.reports.${e.report_id}.name`, { defaultValue: e.report_id })
                        : t(`system:dataExport.dataTypes.${e.data_type}`, { defaultValue: e.data_type })
                    }
                    size="small" variant="outlined"
                    sx={{ borderRadius: 1, height: 20, fontSize: '0.65rem' }}
                  />
                </TableCell>
                <TableCell><Typography variant="caption">{e.format.toUpperCase()}</Typography></TableCell>
                <TableCell><Typography variant="caption" color="text.secondary">{e.schedule ? t(`system:dataExport.schedules.${e.schedule}`, { defaultValue: e.schedule }) : '—'}</Typography></TableCell>
                {/* What the last run produced. Before this the page could only claim a
                    date: the cron generated a file and discarded the URL, so nothing was
                    reachable. `has_file` comes from the row's stored key. */}
                <TableCell>
                  {e.last_run_status === 'failed' ? (
                    <Typography variant="caption" color="error.main">
                      {t('system:dataExport.lastRunFailed')}
                    </Typography>
                  ) : e.has_file ? (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        {t('system:dataExport.lastRunRows', { count: e.last_run_row_count ?? 0 })}
                      </Typography>
                      <Tooltip title={t('system:dataExport.download')}>
                        <IconButton size="small" onClick={() => handleDownload(e)}>
                          <FileDownloadOutlined sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ) : (
                    <Typography variant="caption" color="text.disabled">
                      {t('system:dataExport.neverRun')}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  <Stack direction="row" spacing={0.5} justifyContent="center">
                    <Tooltip title={t('system:dataExport.runNow')}><IconButton size="small" onClick={() => handleManualRun(e)}><PlayArrowOutlined sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                    <IconButton size="small" onClick={() => openEdit(e)}><EditOutlined sx={{ fontSize: 16 }} /></IconButton>
                    <IconButton size="small" onClick={() => setToDelete(e)} sx={{ '&:hover': { color: 'error.main' } }}><DeleteOutlined sx={{ fontSize: 16 }} /></IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ py: 2 }}>{editing ? t('system:dataExport.dialog.editTitle') : t('system:dataExport.dialog.addTitle')}</DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}><TextField fullWidth size="small" label={t('system:dataExport.dialog.nameLabel')} required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></Grid>
            <Grid item xs={6}><FormControl fullWidth size="small"><InputLabel>{t('system:dataExport.dialog.dataTypeLabel')}</InputLabel><Select value={form.data_type} label={t('system:dataExport.dialog.dataTypeLabel')} onChange={e => setForm(f => ({ ...f, data_type: e.target.value }))}>{DATA_TYPES.map(dt => (
                <MenuItem key={dt} value={dt}>
                  {t(`system:dataExport.dataTypes.${dt}`)}
                </MenuItem>
              ))}</Select></FormControl></Grid>
            <Grid item xs={6}><FormControl fullWidth size="small"><InputLabel>{t('system:dataExport.dialog.formatLabel')}</InputLabel><Select value={form.format} label={t('system:dataExport.dialog.formatLabel')} onChange={e => setForm(f => ({ ...f, format: e.target.value }))}>{FORMATS.map(f => <MenuItem key={f} value={f}>{f.toUpperCase()}</MenuItem>)}</Select></FormControl></Grid>
            {form.data_type === REPORT_TYPE && (
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('system:dataExport.dialog.reportLabel')}</InputLabel>
                  <Select
                    value={form.report_id}
                    label={t('system:dataExport.dialog.reportLabel')}
                    onChange={e => setForm(f => ({ ...f, report_id: e.target.value }))}
                  >
                    {reports.map(r => (
                      <MenuItem key={r.id} value={r.id}>
                        {t(`attendance:attendanceReports.reports.${r.id}.name`, { defaultValue: r.name })}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {t('system:dataExport.dialog.reportPeriodHelp')}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12}><FormControl fullWidth size="small"><InputLabel>{t('system:dataExport.dialog.scheduleLabel')}</InputLabel><Select value={form.schedule} label={t('system:dataExport.dialog.scheduleLabel')} onChange={e => setForm(f => ({ ...f, schedule: e.target.value }))}>{SCHEDULES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}</Select></FormControl></Grid>
            <Grid item xs={12}><FormControlLabel control={<Switch size="small" checked={form.enabled} onChange={() => setForm(f => ({ ...f, enabled: !f.enabled }))} />} label={<Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{t('system:dataExport.dialog.enabled')}</Typography>} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>{t('system:shared.cancel')}</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ textTransform: 'none' }}>{saving ? <CircularProgress size={18} /> : editing ? t('system:shared.save') : t('system:shared.create')}</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog open={!!toDelete} tone="danger" title={t('system:dataExport.deleteTitle')} description={t('system:dataExport.deleteDescription')} subject={toDelete && <Typography sx={{ fontWeight: 600 }}>{toDelete.name}</Typography>} confirmLabel={t('system:dataExport.deleteConfirm')} confirmLoadingLabel={t('system:dataExport.deleteLoading')} onConfirm={handleDelete} onClose={() => setToDelete(null)} />
      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}><Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </Box>
  );
};

export default DataExport;
